import type React from 'react';
import { createNewBlock } from '../../../initial-states/textBlocks.initialState';

/**
 * Converts an HTML string to clean plain text.
 * - <a> tags become "text (url)" so links stay visible and useful
 * - <br> and block-level tags become newlines
 * - All other HTML is stripped
 */
function htmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Convert <a> tags: keep link text, append URL if it differs from the text
  doc.querySelectorAll('a').forEach((a) => {
    const href = a.getAttribute('href') ?? '';
    const text = a.textContent?.trim() ?? '';
    if (text === href || text === '') {
      a.replaceWith(href);
    } else {
      a.replaceWith(`${text} (${href})`);
    }
  });

  // Convert <br> to newlines
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));

  // Convert block-level elements to have trailing newlines
  doc
    .querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, tr, blockquote')
    .forEach((el) => {
      el.append('\n');
    });

  return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Extracts clean text from the clipboard, preferring HTML source when
 * available so that hyperlinks are preserved as readable "text (url)"
 * instead of markdown-style [text](url).
 */
function getCleanPastedText(
  event: React.ClipboardEvent<HTMLTextAreaElement>,
): string {
  const html = event.clipboardData.getData('text/html');
  if (html) {
    return htmlToPlainText(html);
  }
  return event.clipboardData.getData('text').trim();
}

/**
 * Handles paste event for a textarea element within a form.
 *
 * 1. Extracts clean text (converting HTML links to readable format)
 * 2. Splits the text by line breaks to create paragraphs
 * 3. Updates the current block with the first paragraph
 * 4. Creates new blocks for any additional paragraphs
 */
export const handlePaste = (
  event: React.ClipboardEvent<HTMLTextAreaElement>,
  setFieldValue: any,
  values: any,
  index: number,
  isEditingDraft: boolean | undefined,
  isAddingDraft: boolean | undefined,
  isEditingPostReplyDraft: boolean | undefined,
) => {
  event.preventDefault();
  const trimmedText = getCleanPastedText(event);

  const paragraphs = trimmedText
    .split(/\r?\n/)
    .filter((para) => para.trim() !== '');

  if (paragraphs.length === 0) return;

  const currentBlocks =
    isEditingDraft || isEditingPostReplyDraft ? values.blocks : values;
  const currentBlock = currentBlocks[index];

  const updatedCurrentBlock = {
    ...currentBlock,
    data: { ...currentBlock.data, paragraph: paragraphs[0] },
  };

  if (paragraphs.length > 1) {
    const newBlocks = paragraphs.slice(1).map((paragraph) => ({
      ...createNewBlock('Paragraph'),
      data: { paragraph: paragraph.trim() },
    }));

    const updatedBlocks = [
      ...currentBlocks.slice(0, index),
      updatedCurrentBlock,
      ...newBlocks,
      ...currentBlocks.slice(index + 1),
    ];

    setFieldValue('blocks', updatedBlocks);
  } else {
    setFieldValue(`blocks.${index}`, updatedCurrentBlock);
  }
};
